// Step 5: Contract Success Component

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ArrowDownTrayIcon, ArrowPathIcon, EnvelopeIcon, HomeIcon } from '@heroicons/react/24/outline';
import { hiringService } from '@/services/hiringService';

interface ContractSuccessProps {
  hiringCode: string;
  serviceName: string;
  userEmail: string;
}

export function ContractSuccess({ hiringCode, serviceName, userEmail }: ContractSuccessProps) {
  const { t } = useTranslation('hiring');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isBrowser = typeof window !== 'undefined';
  const manualPaymentNote = isBrowser ? localStorage.getItem(`manual_payment_note_${hiringCode}`) : null;
  const manualPaymentDate = isBrowser ? localStorage.getItem(`manual_payment_date_${hiringCode}`) : null;

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);

    try {
      console.log('📥 Intentando descargar contrato definitivo desde el backend...');
      
      // Primero intentar descargar el contrato DEFINITIVO (final-contract)
      let blob: Blob;
      try {
        blob = await hiringService.downloadFinalContract(hiringCode);
        console.log('✅ Contrato definitivo descargado desde /final-contract/download');
      } catch (finalErr: any) {
        // Si es 404, significa que el contrato definitivo no existe aún
        const is404 = finalErr?.response?.status === 404 || 
                      finalErr?.message?.includes('404') ||
                      finalErr?.message?.includes('no disponible');
        
        if (is404) {
          console.warn('⚠️ Contrato definitivo no disponible (404), generando localmente...');
          // Generar localmente si el contrato definitivo no existe
          await generateLocalContract();
          console.log('✅ Contrato generado localmente como fallback');
          return; // Salir después de generar localmente
        }
        
        // Si no es 404, intentar endpoint legacy
        console.warn('⚠️ Error descargando contrato definitivo, intentando endpoint legacy...');
        blob = await hiringService.downloadContract(hiringCode);
        console.log('✅ Contrato descargado desde /contract/download (legacy)');
      }
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrato-${serviceName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      console.log('✅ Descarga completada');
    } catch (err: any) {
      console.warn('⚠️ No se pudo descargar desde el backend, generando localmente:', err);
      console.log('🔧 Iniciando generación local de contrato...');
      
      // Si falla la descarga del backend, generar localmente
      try {
        await generateLocalContract();
        console.log('✅ Contrato generado localmente exitosamente');
      } catch (localErr) {
        console.error('❌ Error generando contrato local:', localErr);
        setError(t('success.generateError'));
      }
    } finally {
      setDownloading(false);
    }
  };

  const generateLocalContract = async () => {
    console.log('🔧 Iniciando generateLocalContract...');
    
    // Importar dinámicamente para evitar problemas de bundle
    const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
    console.log('✅ generateContractPDF importado correctamente');
    
    // Obtener datos del localStorage
    const hiringDetails = localStorage.getItem(`hiring_details_${hiringCode}`);
    const clientSignature = localStorage.getItem(`client_signature_${hiringCode}`);
    
    // Obtener firmas manuscritas con fallback al backend
    const { hiringProgressService } = await import('@/services/hiringProgressService');
    const handwrittenSignatures = await hiringProgressService.getHandwrittenSignatures(hiringCode);

    console.log('📋 Datos del localStorage:', {
      hiringDetails: hiringDetails ? 'Encontrado' : 'No encontrado',
      clientSignature: clientSignature ? 'Encontrado' : 'No encontrado',
      handwrittenSignatures: handwrittenSignatures ? Object.keys(handwrittenSignatures).length + ' firmas' : 'No encontradas'
    });
    
    if (!hiringDetails) {
      throw new Error('No se encontraron los detalles de contratación');
    }
    
    const details = JSON.parse(hiringDetails);
    console.log('📄 Detalles parseados:', details);
    
    const manualPaymentNote = localStorage.getItem(`manual_payment_note_${hiringCode}`);
    const manualPaymentDate = localStorage.getItem(`manual_payment_date_${hiringCode}`);
    const manualPaymentMethod = localStorage.getItem(`manual_payment_method_${hiringCode}`);
    
    // Cargar anexos si existen
    let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
    try {
      const { hiringService } = await import('@/services/hiringService');
      const loadedAnnexes = await hiringService.getAnnexes(hiringCode);
      if (loadedAnnexes.length > 0) {
        annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
        console.log('📎 Anexos cargados para descarga:', annexesToInclude.length);
      }
    } catch (annexError) {
      console.warn('No se pudieron cargar anexos:', annexError);
    }
    
    // Generar PDF localmente
    console.log('🔄 Generando PDF localmente...');
    const { getMigroSignatureFromStorage } = await import('@/config/migroSignatureStorage');
    const contractBlob = generateContractPDF(details, {
      paymentIntentId: manualPaymentNote ? 'manual_payment' : 'pi_local_generated',
      stripeTransactionId: manualPaymentNote ? `manual_${Date.now()}` : `local_${Date.now()}`,
      paymentDate: manualPaymentDate || new Date().toISOString(),
      paymentMethod: manualPaymentMethod || 'Generado localmente',
      paymentNote: manualPaymentNote || undefined,
      clientSignature: clientSignature || undefined,
      handwrittenSignatures,
      migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
    }, false, annexesToInclude, { language: details?.language || 'es' }); // isDraft = false, con idioma
    console.log('✅ PDF generado, tamaño:', contractBlob.size, 'bytes');
    
    // Descargar el PDF generado
    console.log('⬇️ Iniciando descarga...');
    const url = window.URL.createObjectURL(contractBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato-${serviceName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    console.log('✅ Descarga completada');
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Card className="shadow-lg border-2 border-green-200">
        <CardHeader className="bg-gradient-to-r from-green-50 to-primary/10">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="bg-green-100 p-6 rounded-full">
                <CheckCircleIcon className="text-green-600" width={64} height={64} />
              </div>
            </div>
            <CardTitle className="text-3xl text-emphasis-900">
              {t('success.title')}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="pt-8 space-y-8">
          {/* Mensaje de éxito */}
          <div className="text-center">
            <p className="text-lg text-gray-700 mb-2">
              {t('success.subtitle')}
            </p>
            <p className="text-2xl font-bold text-primary mb-6">
              {serviceName}
            </p>
          </div>

          {/* Pasos completados */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-4 text-emphasis-900">
              {t('success.stepsCompleted')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" width={20} height={20} />
                <span className="text-gray-700">{t('success.stepData')}</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" width={20} height={20} />
                <span className="text-gray-700">{t('success.stepSigned')}</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" width={20} height={20} />
                <span className="text-gray-700">{t('success.stepPaid')}</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" width={20} height={20} />
                <span className="text-gray-700">{t('success.stepGenerated')}</span>
              </div>
            </div>
          </div>

          {/* Información del contrato */}
          <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <EnvelopeIcon className="text-primary mt-1 flex-shrink-0" width={20} height={20} />
              <div>
                <h3 className="font-semibold text-emphasis-900 mb-1">
                  {t('success.emailSent')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('success.emailSentDesc')}{' '}
                  <span className="font-semibold text-gray-900">{userEmail}</span>
                </p>
              </div>
            </div>
          </div>

          {manualPaymentNote && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-900 space-y-1">
              <p>
                <strong>{t('success.manualPayment')}</strong> {manualPaymentNote}
              </p>
              {manualPaymentDate && (
                <p className="text-xs text-yellow-800">
                  {t('success.manualPaymentDate', { date: new Date(manualPaymentDate).toLocaleDateString('es-ES') })}
                </p>
              )}
            </div>
          )}

          {/* Botón de descarga */}
          <div className="text-center">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-primary hover:bg-primary-700 text-white px-8"
              size="lg"
            >
              {downloading ? (
                <>
                  <ArrowPathIcon className="mr-2 animate-spin" width={20} height={20} />
                  {t('success.downloading')}
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="mr-2" width={20} height={20} />
                  {t('success.downloadContract')}
                </>
              )}
            </Button>
            
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                <p className="text-sm">{error}</p>
                <p className="text-xs mt-2 text-red-600">
                  {t('success.downloadError')}
                </p>
              </div>
            )}
          </div>

          {/* Próximos pasos */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-3">
              {t('success.nextStepsTitle')}
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• {t('success.nextStep1')}</li>
              <li>• {t('success.nextStep2')}</li>
              <li>• {t('success.nextStep3')}</li>
              <li>• {t('success.nextStep4')}</li>
            </ul>
          </div>

          {/* Botón volver al inicio */}
          <div className="text-center pt-4">
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              size="lg"
            >
              <HomeIcon className="mr-2" width={18} height={18} />
              {t('success.backHome')}
            </Button>
          </div>

          {/* Mensaje de agradecimiento */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {t('success.thankYou')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

